<template>
    <div
        :class="['bo-dropdown-search', dropdownClass]"
        :style="primaryColor ? { '--sm-accent': primaryColor } : {}"
    >
        <!-- Toggle button (Trigger) -->
        <div
            class="bo-dropdown-toggle cursor-pointer"
            :class="{ disabled: !editable }"
            :data-toggle="isMobile ? null : 'dropdown'"
            @click="handleToggleClick"
        >
            <!-- Selected method (closed state) -->
            <div
                v-if="value"
                class="option d-flex align-items-center"
                :class="isAvailableOptions(value)"
            >
                <div class="option-content d-flex flex-column">
                    <div class="option-head d-flex align-items-center">
                        <div class="brand text-medium">{{ value.name }}</div>
                    </div>
                    <bo-shipping-method-labels
                        :method="value"
                        :translations="translations"
                        :currency-symbol="currencySymbol"
                        :decimal="decimal"
                        :get-all-shipping-classes="getAllShippingClasses"
                        :get-shipping-method-min-table-rate-label="getShippingMethodMinTableRateLabel"
                        :get-shipping-method-max-table-rate-label="getShippingMethodMaxTableRateLabel"
                        :shipping-method-uses="shippingMethodUses"
                        :single-line="true"
                    ></bo-shipping-method-labels>
                    <div
                        v-if="value.unavailableMessage"
                        class="option-message d-flex"
                        :class="messageClass(value)"
                    >
                        <i class="icon icon-info"></i>
                        <span>{{ value.unavailableMessage }}</span>
                    </div>
                </div>
                <!-- Outside the text column so the price centres on the whole control, like the
                     chevron, instead of riding the first line of the block. -->
                <div
                    v-if="canReadPrices"
                    class="price text-medium"
                >
                    <bo-cell-currency
                        :value="selectedShippingFee"
                        :currency="currencySymbol"
                        :decimal="feeDecimal(selectedShippingFee)"
                    ></bo-cell-currency>
                </div>
                <div class="option-indicator">
                    <i class="icon icon-arrowdropdown"></i>
                </div>
            </div>

            <!-- No method selected (closed state) -->
            <div
                v-else
                class="option d-flex align-items-center"
            >
                <div class="option-content d-flex flex-column">
                    <div class="option-head d-flex align-items-center">
                        <div class="brand text-medium text-muted">
                            {{ translations.select_shipping_method ?? "Select" }}
                        </div>
                    </div>
                </div>
                <div class="option-indicator">
                    <i class="icon icon-arrowdropdown"></i>
                </div>
            </div>
        </div>

        <!-- Desktop: Standard Dropdown Menu -->
        <div
            v-show="!isMobile"
            class="dropdown-menu quick-search"
        >
            <div class="searchbar" @click.stop>
                <div class="btn-search"><i class="icon-search"></i></div>
                <input
                    type="text"
                    v-model="searchQuery"
                    :placeholder="translations.search ?? 'Search'"
                    class="search-input dropdown"
                />
            </div>

            <!-- Empty-state banner -->
            <div
                v-if="!hasSelectableMethods && incompatibleMethods.length === 0"
                class="shipping-method-empty-notice"
            >
                <i class="icon-info"></i>
                <span>{{
                    translations.no_shipping_methods_available ?? "No shipping methods are available for your cart"
                }}</span>
            </div>

            <!-- If searching and no results match -->
            <div
                v-else-if="searchQuery && !hasSearchResults"
                class="shipping-method-empty-notice"
            >
                <i class="icon-info"></i>
                <span>{{ translations.no_results ?? translations.no_results_found ?? "No results found" }}</span>
            </div>

            <div class="shipping-method-dropdown-content" v-else>
                <!-- Available Section -->
                <div
                    v-if="displayedDesktopAvailableMethods.length > 0"
                    class="shipping-method-section"
                >
                    <div class="shipping-method-group-title">
                        {{ translations.available ?? "Available" }}
                    </div>
                    <div
                        v-for="item in displayedDesktopAvailableMethods"
                        :key="item.id"
                        class="dropdown-item shipping-method-option d-flex align-items-start"
                        :class="[isAvailableOptions(item), { selected: isSelected(item) }]"
                        @click="selectDesktopMethod(item)"
                    >
                        <div class="option-radio">
                            <span
                                class="custom-radio"
                                :class="{ checked: isSelected(item), disabled: item.disabled }"
                            ></span>
                        </div>
                        <div class="option-content">
                            <div class="option-head">
                                <div class="brand text-medium">{{ item.name }}</div>
                                <div
                                    v-if="canReadPrices"
                                    class="price text-medium"
                                >
                                    <bo-cell-currency
                                        :value="itemShippingFee(item)"
                                        :currency="currencySymbol"
                                        :decimal="feeDecimal(itemShippingFee(item))"
                                    ></bo-cell-currency>
                                </div>
                            </div>
                            <bo-shipping-method-labels
                                :method="item"
                                :translations="translations"
                                :currency-symbol="currencySymbol"
                                :decimal="decimal"
                                :get-all-shipping-classes="getAllShippingClasses"
                                :get-shipping-method-min-table-rate-label="getShippingMethodMinTableRateLabel"
                                :get-shipping-method-max-table-rate-label="getShippingMethodMaxTableRateLabel"
                                :shipping-method-uses="shippingMethodUses"
                            ></bo-shipping-method-labels>
                            <div
                                v-if="item.unavailableMessage && isSelected(item)"
                                class="option-message d-flex"
                                :class="messageClass(item)"
                            >
                                <i class="icon icon-info"></i>
                                <span>{{ item.unavailableMessage }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- Show More / Show Less Toggle for Available Methods -->
                    <div
                        v-if="!searchQuery && availableMethods.length > 1"
                        class="shipping-method-toggle text-center"
                        @click.stop="toggleMoreAvailable"
                    >
                        <span>{{ showMoreAvailable ? (translations.show_less ?? "Show Less") : showMoreText }}</span>
                        <i :class="showMoreAvailable ? 'icon-arrowdropup' : 'icon-arrowdropdown'"></i>
                    </div>
                </div>

                <!-- Not Yet Met (Adjustable) Section -->
                <div
                    v-if="displayedDesktopAdjustableMethods.length > 0"
                    class="shipping-method-section"
                    :class="{ 'has-divider-top': displayedDesktopAvailableMethods.length > 0 }"
                >
                    <div class="shipping-method-group-title">
                        {{ translations.not_yet_met ?? "Not Yet Met" }}
                    </div>
                    <div
                        v-for="item in displayedDesktopAdjustableMethods"
                        :key="item.id"
                        class="dropdown-item shipping-method-option d-flex align-items-start"
                        :class="[isAvailableOptions(item), { selected: isSelected(item) }]"
                        @click="selectDesktopMethod(item)"
                    >
                        <div class="option-radio">
                            <span
                                class="custom-radio"
                                :class="{ checked: isSelected(item), disabled: item.disabled }"
                            ></span>
                        </div>
                        <div class="option-content">
                            <div class="option-head">
                                <div class="brand text-medium">{{ item.name }}</div>
                                <div
                                    v-if="canReadPrices"
                                    class="price text-medium"
                                >
                                    <bo-cell-currency
                                        :value="itemShippingFee(item)"
                                        :currency="currencySymbol"
                                        :decimal="feeDecimal(itemShippingFee(item))"
                                    ></bo-cell-currency>
                                </div>
                            </div>
                            <bo-shipping-method-labels
                                :method="item"
                                :translations="translations"
                                :currency-symbol="currencySymbol"
                                :decimal="decimal"
                                :get-all-shipping-classes="getAllShippingClasses"
                                :get-shipping-method-min-table-rate-label="getShippingMethodMinTableRateLabel"
                                :get-shipping-method-max-table-rate-label="getShippingMethodMaxTableRateLabel"
                                :shipping-method-uses="shippingMethodUses"
                            ></bo-shipping-method-labels>
                            <div
                                v-if="item.unavailableMessage && isSelected(item)"
                                class="option-message d-flex"
                                :class="messageClass(item)"
                            >
                                <i class="icon icon-info"></i>
                                <span>{{ item.unavailableMessage }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Unavailable (Incompatible) Section -->
                <div
                    v-if="showUnavailableToggle"
                    class="shipping-method-section"
                    :class="{ 'has-divider-top': displayedDesktopAvailableMethods.length > 0 || displayedDesktopAdjustableMethods.length > 0 }"
                >
                    <div
                        class="shipping-method-toggle text-center"
                        @click.stop="$emit('toggle-unavailable')"
                    >
                        <span>{{
                            showUnavailable
                                ? (translations.hide_unavailable_options ?? "Hide Unavailable Options")
                                : (translations.show_unavailable_options ?? "Show Unavailable Options")
                        }}</span>
                        <i :class="showUnavailable ? 'icon-arrowdropup' : 'icon-arrowdropdown'"></i>
                    </div>

                    <div v-if="showUnavailable && displayedDesktopIncompatibleMethods.length > 0">
                        <div
                            v-for="item in displayedDesktopIncompatibleMethods"
                            :key="item.id"
                            class="dropdown-item shipping-method-option incompatible d-flex align-items-start"
                            :class="[isAvailableOptions(item), { selected: isSelected(item) }]"
                        >
                            <div class="option-radio">
                                <span
                                    class="custom-radio"
                                    :class="{ checked: isSelected(item), disabled: true }"
                                ></span>
                            </div>
                            <div class="option-content">
                                <div class="option-head">
                                    <div class="brand text-medium">{{ item.name }}</div>
                                    <div
                                        v-if="canReadPrices"
                                        class="price text-medium"
                                    >
                                        <bo-cell-currency
                                            :value="itemShippingFee(item)"
                                            :currency="currencySymbol"
                                            :decimal="feeDecimal(itemShippingFee(item))"
                                        ></bo-cell-currency>
                                    </div>
                                </div>
                                <bo-shipping-method-labels
                                    :method="item"
                                    :translations="translations"
                                    :currency-symbol="currencySymbol"
                                    :decimal="decimal"
                                    :get-all-shipping-classes="getAllShippingClasses"
                                    :get-shipping-method-min-table-rate-label="getShippingMethodMinTableRateLabel"
                                    :get-shipping-method-max-table-rate-label="getShippingMethodMaxTableRateLabel"
                                    :shipping-method-uses="shippingMethodUses"
                                ></bo-shipping-method-labels>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Mobile: Popup Modal -->
        <bo-popup-modal
            :active="isModalOpen"
            :title="translations.shipping_method ?? translations.shipping_methods ?? 'Shipping Method'"
            :show-modal-footer="false"
            :action="null"
            :append-to-body="true"
            modal-style="shipping-method-mobile-modal"
            :style="primaryColor ? { '--sm-accent': primaryColor } : {}"
            @deactivate="isModalOpen = false"
        >
            <template #body-content>
                <div class="shipping-method-mobile-content">
                    <div class="searchbar">
                        <div class="btn-search"><i class="icon-search"></i></div>
                        <input
                            type="text"
                            v-model="searchQuery"
                            :placeholder="translations.search ?? 'Search'"
                            class="search-input"
                        />
                    </div>

                    <!-- Empty-state banner -->
                    <div
                        v-if="!hasSelectableMethods && incompatibleMethods.length === 0"
                        class="shipping-method-empty-notice"
                    >
                        <i class="icon-info"></i>
                        <span>{{
                            translations.no_shipping_methods_available ?? "No shipping methods are available for your cart"
                        }}</span>
                    </div>

                    <!-- If searching and no results match -->
                    <div
                        v-else-if="searchQuery && !hasSearchResults"
                        class="shipping-method-empty-notice"
                    >
                        <i class="icon-info"></i>
                        <span>{{ translations.no_results ?? translations.no_results_found ?? "No results found" }}</span>
                    </div>

                    <!-- Available Section -->
                    <div
                        v-if="displayedMobileAvailableMethods.length > 0"
                        class="shipping-method-modal-section"
                    >
                        <div class="shipping-method-modal-group-title">
                            {{ translations.available ?? "Available" }}
                        </div>
                        <div
                            v-for="item in displayedMobileAvailableMethods"
                            :key="item.id"
                            class="shipping-method-modal-option"
                            :class="[isAvailableOptions(item), { selected: isSelected(item) }]"
                            @click="selectMobileMethod(item)"
                        >
                            <div class="option-radio">
                                <span
                                    class="custom-radio"
                                    :class="{ checked: isSelected(item), disabled: item.disabled }"
                                ></span>
                            </div>
                            <div class="option-content">
                                <div class="option-head">
                                    <div class="brand text-medium">{{ item.name }}</div>
                                    <div
                                        v-if="canReadPrices"
                                        class="price text-medium"
                                    >
                                        <bo-cell-currency
                                            :value="itemShippingFee(item)"
                                            :currency="currencySymbol"
                                            :decimal="feeDecimal(itemShippingFee(item))"
                                        ></bo-cell-currency>
                                    </div>
                                </div>
                                <bo-shipping-method-labels
                                    :method="item"
                                    :translations="translations"
                                    :currency-symbol="currencySymbol"
                                    :decimal="decimal"
                                    :get-all-shipping-classes="getAllShippingClasses"
                                    :get-shipping-method-min-table-rate-label="getShippingMethodMinTableRateLabel"
                                    :get-shipping-method-max-table-rate-label="getShippingMethodMaxTableRateLabel"
                                    :shipping-method-uses="shippingMethodUses"
                                ></bo-shipping-method-labels>
                                <div
                                    v-if="item.unavailableMessage && isSelected(item)"
                                    class="option-message d-flex"
                                    :class="messageClass(item)"
                                >
                                    <i class="icon icon-info"></i>
                                    <span>{{ item.unavailableMessage }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- Show More / Show Less Toggle for Available Methods -->
                        <div
                            v-if="availableMethods.length > 1 && !searchQuery"
                            class="shipping-method-modal-toggle text-center"
                            @click.stop="toggleMoreAvailable"
                        >
                            <span>{{ showMoreAvailable ? (translations.show_less ?? "Show Less") : showMoreText }}</span>
                            <i :class="showMoreAvailable ? 'icon-arrowdropup' : 'icon-arrowdropdown'"></i>
                        </div>
                    </div>

                    <!-- Not Yet Met (Adjustable) Section -->
                    <div
                        v-if="filteredAdjustableMethods.length > 0"
                        class="shipping-method-modal-section"
                    >
                        <div class="shipping-method-modal-group-title">
                            {{ translations.not_yet_met ?? "Not Yet Met" }}
                        </div>
                        <div
                            v-for="item in filteredAdjustableMethods"
                            :key="item.id"
                            class="shipping-method-modal-option"
                            :class="[isAvailableOptions(item), { selected: isSelected(item) }]"
                            @click="selectMobileMethod(item)"
                        >
                            <div class="option-radio">
                                <span
                                    class="custom-radio"
                                    :class="{ checked: isSelected(item), disabled: item.disabled }"
                                ></span>
                            </div>
                            <div class="option-content">
                                <div class="option-head">
                                    <div class="brand text-medium">{{ item.name }}</div>
                                    <div
                                        v-if="canReadPrices"
                                        class="price text-medium"
                                    >
                                        <bo-cell-currency
                                            :value="itemShippingFee(item)"
                                            :currency="currencySymbol"
                                            :decimal="feeDecimal(itemShippingFee(item))"
                                        ></bo-cell-currency>
                                    </div>
                                </div>
                                <bo-shipping-method-labels
                                    :method="item"
                                    :translations="translations"
                                    :currency-symbol="currencySymbol"
                                    :decimal="decimal"
                                    :get-all-shipping-classes="getAllShippingClasses"
                                    :get-shipping-method-min-table-rate-label="getShippingMethodMinTableRateLabel"
                                    :get-shipping-method-max-table-rate-label="getShippingMethodMaxTableRateLabel"
                                    :shipping-method-uses="shippingMethodUses"
                                ></bo-shipping-method-labels>
                                <div
                                    v-if="item.unavailableMessage && isSelected(item)"
                                    class="option-message d-flex"
                                    :class="messageClass(item)"
                                >
                                    <i class="icon icon-info"></i>
                                    <span>{{ item.unavailableMessage }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Unavailable (Incompatible) Section -->
                    <div
                        v-if="showUnavailableToggle && hasSearchResults"
                        class="shipping-method-modal-section"
                    >
                        <div
                            class="shipping-method-modal-toggle text-center"
                            @click.stop="$emit('toggle-unavailable')"
                        >
                            <span>{{
                                showUnavailable
                                    ? (translations.hide_unavailable_options ?? "Hide Unavailable Options")
                                    : (translations.show_unavailable_options ?? "Show Unavailable Options")
                            }}</span>
                            <i :class="showUnavailable ? 'icon-arrowdropup' : 'icon-arrowdropdown'"></i>
                        </div>

                        <div v-if="showUnavailable && filteredIncompatibleMethods.length > 0">
                            <div
                                v-for="item in filteredIncompatibleMethods"
                                :key="item.id"
                                class="shipping-method-modal-option incompatible"
                                :class="[isAvailableOptions(item), { selected: isSelected(item) }]"
                            >
                                <div class="option-radio">
                                    <span
                                        class="custom-radio"
                                        :class="{ checked: isSelected(item), disabled: true }"
                                    ></span>
                                </div>
                                <div class="option-content">
                                    <div class="option-head">
                                        <div class="brand text-medium">{{ item.name }}</div>
                                        <div
                                            v-if="canReadPrices"
                                            class="price text-medium"
                                        >
                                            <bo-cell-currency
                                                :value="itemShippingFee(item)"
                                                :currency="currencySymbol"
                                                :decimal="feeDecimal(itemShippingFee(item))"
                                            ></bo-cell-currency>
                                        </div>
                                    </div>
                                    <bo-shipping-method-labels
                                        :method="item"
                                        :translations="translations"
                                        :currency-symbol="currencySymbol"
                                        :decimal="decimal"
                                        :get-all-shipping-classes="getAllShippingClasses"
                                        :get-shipping-method-min-table-rate-label="getShippingMethodMinTableRateLabel"
                                        :get-shipping-method-max-table-rate-label="getShippingMethodMaxTableRateLabel"
                                        :shipping-method-uses="shippingMethodUses"
                                    ></bo-shipping-method-labels>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </bo-popup-modal>
    </div>
</template>

<script>
import boShippingMethodLabels from "@/v2/components/bo-shipping-method-labels.vue";
import boPopupModal from "@/v2/components/bo-popup-modal.vue";
import { getShippingFeeDecimalPlaces } from "@/v2/utils/utilities.js";

export default {
    name: "bo-shipping-method-dropdown",
    components: {
        boShippingMethodLabels,
        boPopupModal,
    },
    model: {
        prop: "value",
        event: "change",
    },
    props: {
        items: {
            type: Array,
            default: () => [],
        },
        value: {
            type: Object,
            default: null,
        },
        editable: {
            type: Boolean,
            default: true,
        },
        currencySymbol: {
            type: String,
            default: "",
        },
        decimal: {
            type: [Number, String],
            default: 2,
        },
        // Site currency decimal places; 0 (TWD) hides the decimals on whole-number fees.
        siteCurrencyDecimalPlaces: {
            type: [Number, String],
            default: 2,
        },
        // Whether shipping prices should be shown (capability gate on document pages).
        canReadPrices: {
            type: Boolean,
            default: true,
        },
        shippingMethodUses: {
            type: Object,
            default: () => ({}),
        },
        shippingFees: {
            type: Object,
            default: () => ({}),
        },
        shippingFeeTaxClass: {
            default: null,
        },
        // Fee shown for the selected (closed) method — pages compute this differently.
        selectedShippingFee: {
            type: [Number, String],
            default: 0,
        },
        // Display helpers from the parent (mixins differ per page).
        getAllShippingClasses: {
            type: Function,
            default: () => "",
        },
        getShippingMethodMinTableRateLabel: {
            type: Function,
            default: () => "",
        },
        getShippingMethodMaxTableRateLabel: {
            type: Function,
            default: () => "",
        },
        addOrRemoveTaxIfNeeded: {
            type: Function,
            default: (taxClass, value) => value,
        },
        showUnavailable: {
            type: Boolean,
            default: false,
        },
        hasHiddenUnavailable: {
            type: Boolean,
            default: false,
        },
        // Extra classes forwarded to bo-dropdown-search (page-specific control styling).
        dropdownStyle: {
            type: String,
            default: "",
        },
        primaryColor: {
            type: String,
            default: "",
        },
        translations: {
            type: Object,
            default: () => ({}),
        },
        variant: {
            type: String,
            default: "default",
        },
    },
    data() {
        return {
            searchQuery: null,
            isModalOpen: false,
            showMoreAvailable: false,
            isMobile: false,
        };
    },
    computed: {
        dropdownClass() {
            return [this.dropdownStyle, "shipping-method-dropdown"].filter(Boolean).join(" ");
        },
        resultQuery() {
            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase().trim();
                const result = [];
                (this.items || []).forEach((element) => {
                    result.push({
                        items: (element.items || []).filter((item) => {
                            if (item.isToggle) return true;
                            if (typeof item.hasQuery === "function") {
                                return item.hasQuery(query);
                            }
                            return (item.name || item.value || "").toLowerCase().includes(query);
                        }),
                        groupTitle: element.groupTitle,
                    });
                });
                return result;
            }
            return this.items || [];
        },
        allFlattenedMethods() {
            const list = [];
            (this.items || []).forEach((group) => {
                if (Array.isArray(group.items)) {
                    group.items.forEach((item) => {
                        if (item && !item.isToggle) {
                            list.push(item);
                        }
                    });
                }
            });
            return list;
        },
        hasSelectableMethods() {
            return (this.items || []).some(
                (group) => Array.isArray(group.items) && group.items.some((item) => !item.isToggle && !item.disabled)
            );
        },
        availableMethods() {
            return this.allFlattenedMethods.filter(
                (item) => item.availabilityTier === "available"
            );
        },
        sortedAvailableMethods() {
            if (!this.value) return this.availableMethods;
            const selected = this.availableMethods.find((m) => this.isSelected(m));
            if (!selected) return this.availableMethods;
            return [selected, ...this.availableMethods.filter((m) => !this.isSelected(m))];
        },
        visibleAvailableMethods() {
            if (this.showMoreAvailable || this.sortedAvailableMethods.length <= 1) {
                return this.sortedAvailableMethods;
            }
            return [this.sortedAvailableMethods[0]];
        },
        hiddenAvailableCount() {
            return Math.max(0, this.availableMethods.length - this.visibleAvailableMethods.length);
        },
        adjustableMethods() {
            return this.allFlattenedMethods.filter(
                (item) => item.availabilityTier === "adjustable"
            );
        },
        incompatibleMethods() {
            return this.allFlattenedMethods.filter(
                (item) => item.availabilityTier === "incompatible"
            );
        },
        showUnavailableToggle() {
            return (
                this.hasHiddenUnavailable ||
                this.incompatibleMethods.length > 0 ||
                (this.items || []).some(
                    (group) => Array.isArray(group.items) && group.items.some((item) => item.isToggle)
                )
            );
        },
        filteredAvailableMethods() {
            if (!this.searchQuery) return this.sortedAvailableMethods;
            const q = this.searchQuery.toLowerCase().trim();
            return this.sortedAvailableMethods.filter((item) => this.matchesQuery(item, q));
        },
        displayedDesktopAvailableMethods() {
            if (this.searchQuery) {
                return this.filteredAvailableMethods;
            }
            return this.visibleAvailableMethods;
        },
        // The modal shows the same methods as the desktop menu, so a query has to reveal every
        // match rather than the collapsed "show more" cap.
        displayedMobileAvailableMethods() {
            if (this.searchQuery) {
                return this.filteredAvailableMethods;
            }
            return this.visibleAvailableMethods;
        },
        filteredAdjustableMethods() {
            if (!this.searchQuery) return this.adjustableMethods;
            const q = this.searchQuery.toLowerCase().trim();
            return this.adjustableMethods.filter((item) => this.matchesQuery(item, q));
        },
        displayedDesktopAdjustableMethods() {
            return this.filteredAdjustableMethods;
        },
        filteredIncompatibleMethods() {
            if (!this.searchQuery) return this.incompatibleMethods;
            const q = this.searchQuery.toLowerCase().trim();
            return this.incompatibleMethods.filter((item) => this.matchesQuery(item, q));
        },
        displayedDesktopIncompatibleMethods() {
            return this.filteredIncompatibleMethods;
        },
        hasSearchResults() {
            if (!this.searchQuery) return true;
            return (
                this.filteredAvailableMethods.length > 0 ||
                this.filteredAdjustableMethods.length > 0 ||
                (this.showUnavailable && this.filteredIncompatibleMethods.length > 0)
            );
        },
        showMoreText() {
            const count = this.hiddenAvailableCount;
            if (this.translations.show_more_count) {
                return this.translations.show_more_count.replace(":count", count);
            }
            const show = this.translations.show ?? "Show";
            const more = this.translations.more ?? "More";
            return `${show} ${count} ${more}`;
        },
    },
    watch: {
        isModalOpen(newVal) {
            if (!newVal) {
                this.showMoreAvailable = false;
                this.searchQuery = null;
            }
        },
        isMobile(newVal) {
            if (!newVal && typeof window !== "undefined" && window.$) {
                const toggle = this.$el?.querySelector?.('[data-toggle="dropdown"]');
                if (toggle) {
                    window.$(toggle).dropdown("dispose");
                }
            }
        },
    },
    created() {
        this.checkIsMobile();
    },
    mounted() {
        this.checkIsMobile();
        if (typeof window !== "undefined") {
            if (typeof window.matchMedia === "function") {
                this.mediaQueryList = window.matchMedia("(max-width: 768px)");
                this.handleMediaChange = () => {
                    this.checkIsMobile();
                };
                if (this.mediaQueryList.addEventListener) {
                    this.mediaQueryList.addEventListener("change", this.handleMediaChange);
                } else if (this.mediaQueryList.addListener) {
                    this.mediaQueryList.addListener(this.handleMediaChange);
                }
            }
            window.addEventListener("resize", this.checkIsMobile);
            window.addEventListener("orientationchange", this.checkIsMobile);
        }
    },
    beforeDestroy() {
        if (this.mediaQueryList && this.handleMediaChange) {
            if (this.mediaQueryList.removeEventListener) {
                this.mediaQueryList.removeEventListener("change", this.handleMediaChange);
            } else if (this.mediaQueryList.removeListener) {
                this.mediaQueryList.removeListener(this.handleMediaChange);
            }
        }
        if (typeof window !== "undefined") {
            window.removeEventListener("resize", this.checkIsMobile);
            window.removeEventListener("orientationchange", this.checkIsMobile);
        }
    },
    methods: {
        checkIsMobile() {
            if (typeof window === "undefined") {
                this.isMobile = false;
                return;
            }
            if (typeof window.isMobileViewport !== "undefined") {
                this.isMobile = !!window.isMobileViewport;
                return;
            }
            const width = window.innerWidth || document.documentElement?.clientWidth || document.body?.clientWidth || 0;
            this.isMobile = width <= 768;
        },
        matchesQuery(item, query) {
            if (!query) return true;
            if (typeof item.hasQuery === "function") {
                return item.hasQuery(query);
            }
            return (item.name || item.value || "").toLowerCase().includes(query);
        },
        handleToggleClick(e) {
            if (!this.editable) {
                return;
            }
            this.checkIsMobile();
            if (this.isMobile) {
                e.stopPropagation();
                e.preventDefault();
                this.isModalOpen = true;
            }
        },
        selectDesktopMethod(method) {
            if (method.disabled) {
                return;
            }

            // Selecting a method refreshes the parent data before Bootstrap's document-click
            // handler can clear the menu. Close it here so selectable "Not Yet Met" rows and
            // available rows always leave the desktop control in its closed state.
            this.closeDesktopDropdown();
            this.$emit("change", method);
        },
        selectMobileMethod(method) {
            if (method.disabled) {
                return;
            }
            this.$emit("change", method);
            this.isModalOpen = false;
        },
        toggleMoreAvailable() {
            this.showMoreAvailable = !this.showMoreAvailable;
        },
        closeDesktopDropdown() {
            if (this.isMobile || typeof window === "undefined" || !window.$) {
                return;
            }

            const toggle = this.$el?.querySelector?.('[data-toggle="dropdown"]');
            if (toggle) {
                window.$(toggle).dropdown("hide");
            }
        },
        feeDecimal(fee) {
            return getShippingFeeDecimalPlaces(fee, this.siteCurrencyDecimalPlaces, Number(this.decimal));
        },
        itemShippingFee(method) {
            return this.addOrRemoveTaxIfNeeded(
                this.shippingFeeTaxClass,
                this.shippingFees[method.id]?.shipping_fee ?? 0
            );
        },
        messageClass(method) {
            return `option-message--${method.availabilityTier}`;
        },
        isAvailableOptions(method) {
            return `${method.availabilityTier}`;
        },
        isSelected(method) {
            return this.value != null && this.value.id === method.id;
        },
    },
};
</script>
